import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import OrganizerLayout from '../OrganizerDashboard/OrganizerLayout';
import userProfileService from '../../services/userProfileService';
import venueDashboardApiService from '../../services/venueDashboardApi';
import messageThreadsApiService from '../../services/messageThreadsApi';
import { buildBookingThreadTopic } from '../../utils/messageThreadDisplay';
import BookingStripePayment from './BookingStripePayment';
import BookingInvoicePayment from './BookingInvoicePayment';
import {
  buildBookingDetailsLink,
  buildBookingPaymentLink,
  formatPaymentStatusLabel,
  canShowOrganizerBookingPricing,
  isApprovedBookingStatus,
  bookingNeedsOrganizerPayment,
  bookingNeedsPendingDepositPayment,
  bookingNeedsStripePaymentNow,
  isDepositPaidBookingPaymentStatus,
  isFreeBooking,
  needsVenuePricingConfirmation,
  resolveBookingPaymentContext,
  canAccessBooking
} from '../../utils/bookingPayment';
import { isAdminUser } from '../../utils/adminUser';
import { redirectForBookingAccessMismatch } from '../../utils/bookingAccessMismatch';
import { buildVenuePath } from '../../utils/venueSlug';
import {
  formatOrganizerCurrency,
  formatOrganizerDate,
  formatOrganizerTimeRange,
  getOrganizerBookingBadge,
} from '../OrganizerDashboard/organizerUtils';
import { IconArrowLeft, IconBookings, IconCalendar, IconClock, IconMessage, IconProfile, IconUsers } from '../OrganizerDashboard/OrganizerIcons';
import {
  formatBookingAv,
  formatBookingCatering,
  formatBookingDietaryRestrictions,
  isInHouseCateringPreference
} from '../../utils/bookingExportDetails';
import '../OrganizerDashboard/OrganizerDashboard.css';
import './Profile.css';

const normalizeIdentity = (value) => (value == null ? '' : String(value).trim().toLowerCase());

const getInvoiceMetaFromBooking = (booking) => {
  const invoiceCandidate = booking?.invoice || booking?.invoice_file || null;
  const url = booking?.invoice_url || invoiceCandidate?.url || invoiceCandidate?.file_url || null;
  const name =
    booking?.invoice_file_name ||
    invoiceCandidate?.name ||
    invoiceCandidate?.file_name ||
    (typeof url === 'string' ? url.split('/').pop() : null) ||
    null;

  if (!url && !name) return null;
  return { url: url || null, name: name || 'Invoice' };
};

const getVenueImageFromSource = (source) =>
  source?.primary_image_url ||
  source?.primaryImageUrl ||
  (Array.isArray(source?.images)
    ? (source.images.find((img) => img?.is_primary)?.image_url || source.images[0]?.image_url)
    : null) ||
  source?.venue_image_url ||
  source?.venueImageUrl ||
  source?.venue_image ||
  source?.image_url ||
  source?.image ||
  '';

const getVenueAddressFromSource = (source) => {
  if (!source) return '';

  const directAddress =
    source?.venueAddress ||
    source?.venue_address ||
    source?.address ||
    source?.area ||
    source?.location ||
    '';

  if (directAddress) return directAddress;

  const addressParts = [
    source?.address_line_1 || source?.addressLine1 || source?.address1,
    source?.address_line_2 || source?.addressLine2 || source?.address2,
    source?.city,
    source?.postal_code || source?.postcode || source?.zip_code
  ].filter(Boolean);

  return addressParts.join(', ');
};

const BookingDetails = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const paymentToken = (searchParams.get('token') || '').trim();
  const adminViewUserId = (searchParams.get('adminViewUserId') || '').trim();
  const navigate = useNavigate();
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const [booking, setBooking] = useState(null);
  const [paymentContext, setPaymentContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentStatusLabel, setPaymentStatusLabel] = useState('');
  const [invoiceActorId, setInvoiceActorId] = useState(null);
  const [invoiceMeta, setInvoiceMeta] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [fallbackVenueData, setFallbackVenueData] = useState(null);
  const [openingMessageThread, setOpeningMessageThread] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [bookingNotes, setBookingNotes] = useState([]);
  const [detailTab, setDetailTab] = useState('details');
  const hasRedirectedForMismatch = useRef(false);

  const cameForPayment = searchParams.get('pay') === 'stripe';
  const fromBookingSubmit = searchParams.get('from') === 'booking';
  const [inquirySentVisible, setInquirySentVisible] = useState(false);

  useEffect(() => {
    if (authLoading || !bookingId || !paymentToken || currentUser) return;
    const params = new URLSearchParams({ token: paymentToken });
    if (cameForPayment) params.set('pay', 'stripe');
    navigate(`/pay/booking/${bookingId}?${params.toString()}`, { replace: true });
  }, [authLoading, bookingId, paymentToken, currentUser, cameForPayment, navigate]);

  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId || !currentUser) return;

      try {
        setLoading(true);
        setError('');

        const jwtToken = localStorage.getItem('access_token');
        if (!jwtToken) {
          throw new Error('Authentication error. Please try logging in again.');
        }

        userProfileService.setAuthToken(jwtToken);
        userProfileService.setUser(currentUser);

        let backendUserId =
          userProfile?.backend_user_id ||
          userProfile?.firebase_uid ||
          currentUser?.uid ||
          null;

        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser.id) {
              backendUserId = parsedUser.id;
            }
          } catch (parseError) {
            // Keep fallback IDs when parsing fails.
          }
        }

        setInvoiceActorId(backendUserId || userProfile?.email || currentUser?.email || null);
        const bookingDetails = await userProfileService.getBookingDetails(bookingId);
        setBooking(bookingDetails);
      } catch (loadError) {
        setError(loadError?.message || 'Failed to load booking details.');
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId, currentUser, userProfile]);

  useEffect(() => {
    if (!bookingId || !booking?.venueId) {
      setPaymentContext(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const forceStripePay = searchParams.get('pay') === 'stripe';
        const context = await resolveBookingPaymentContext(
          bookingId,
          booking.venueId,
          null,
          { forceStripePay, booking }
        );
        if (!cancelled) setPaymentContext(context);
      } catch {
        if (!cancelled) {
          const forceStripePay = searchParams.get('pay') === 'stripe';
          setPaymentContext(
            forceStripePay
              ? {
                  mode: 'occasia_stripe',
                  link: buildBookingPaymentLink(bookingId)
                }
              : {
                  mode: 'invoice',
                  link: buildBookingDetailsLink(bookingId)
                }
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, booking?.venueId, booking, searchParams]);

  const venueApproved = canShowOrganizerBookingPricing(booking);
  const bookingIsFree = isFreeBooking(booking);
  const bookingIsTbc = needsVenuePricingConfirmation(booking);
  const needsPayment = bookingNeedsOrganizerPayment(booking);

  const loggedInEmail = (currentUser?.email || userProfile?.email || '').trim();
  const isAdminView = Boolean(adminViewUserId) && isAdminUser(userProfile);
  const accountMismatch =
    Boolean(booking) &&
    Boolean(currentUser) &&
    !canAccessBooking(booking, { userProfile, currentUser, adminViewUserId });

  // On profile, a logged-in organiser should pay via session JWT — not an old ?token= from email.
  const stripePaymentToken = accountMismatch ? paymentToken : null;

  useEffect(() => {
    if (loading || authLoading || !bookingId || !paymentToken || !currentUser || !booking) return;
    if (accountMismatch) return;
    const params = new URLSearchParams(searchParams);
    if (!params.has('token')) return;
    params.delete('token');
    const query = params.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [
    loading,
    authLoading,
    bookingId,
    paymentToken,
    currentUser,
    booking,
    accountMismatch,
    navigate,
    searchParams
  ]);

  useEffect(() => {
    if (loading || authLoading || !currentUser || !booking || !accountMismatch) return;
    if (hasRedirectedForMismatch.current) return;
    hasRedirectedForMismatch.current = true;
    if (paymentToken) {
      const params = new URLSearchParams({ token: paymentToken });
      if (cameForPayment) params.set('pay', 'stripe');
      navigate(`/pay/booking/${bookingId}?${params.toString()}`, { replace: true });
      return;
    }
    redirectForBookingAccessMismatch(navigate, {
      bookingEmail: booking.contactEmail,
      loggedInEmail,
      returnPath: `${window.location.pathname}${window.location.search}`
    });
  }, [
    loading,
    authLoading,
    currentUser,
    booking,
    accountMismatch,
    loggedInEmail,
    navigate,
    paymentToken,
    cameForPayment,
    bookingId
  ]);

  const pendingDepositDue = bookingNeedsPendingDepositPayment(booking);
  const stripePaymentDue = bookingNeedsStripePaymentNow(booking);
  const autoOpenPayment =
    cameForPayment &&
    paymentContext?.mode === 'occasia_stripe' &&
    !accountMismatch &&
    stripePaymentDue;

  const clearFromBookingQuery = () => {
    const params = new URLSearchParams(searchParams);
    if (!params.has('from')) return;
    params.delete('from');
    const query = params.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  };

  const handleDepositPaymentComplete = () => {
    setInquirySentVisible(true);
    clearFromBookingQuery();
  };

  const handleBackToBookings = async () => {
    if (pendingDepositDue && !inquirySentVisible && bookingId) {
      const leave = window.confirm(
        'Your request has not been sent to the venue yet. Cancel this booking and go back?'
      );
      if (!leave) {
        return;
      }
      try {
        await userProfileService.cancelBooking(bookingId, 'Deposit not paid');
      } catch (cancelErr) {
        setError(cancelErr?.message || 'Could not cancel booking.');
        return;
      }
    }
    navigate('/profile?section=bookings');
  };

  useEffect(() => {
    if (!booking || !fromBookingSubmit) return;
    const paymentStatus = String(booking.paymentStatus ?? booking.payment_status ?? '').toLowerCase();
    if (isDepositPaidBookingPaymentStatus(paymentStatus) || paymentStatus === 'paid') {
      setInquirySentVisible(true);
      clearFromBookingQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when booking payment status loads
  }, [booking?.paymentStatus, booking?.payment_status, fromBookingSubmit]);

  useEffect(() => {
    const loadNotes = async () => {
      if (!bookingId || !invoiceActorId) {
        setBookingNotes([]);
        return;
      }
      try {
        const notes = await userProfileService.getBookingNotes(bookingId, invoiceActorId);
        setBookingNotes(Array.isArray(notes) ? notes : []);
      } catch {
        setBookingNotes([]);
      }
    };
    loadNotes();
  }, [bookingId, invoiceActorId]);

  useEffect(() => {
    const loadVenueFallback = async () => {
      const imageFromBooking = getVenueImageFromSource(booking);
      const addressFromBooking = getVenueAddressFromSource(booking);
      if (imageFromBooking && addressFromBooking) {
        setFallbackVenueData(null);
        return;
      }

      if (!booking?.venueId) {
        setFallbackVenueData(null);
        return;
      }

      try {
        const venue = await venueDashboardApiService.getVenueDetails(booking.venueId);
        setFallbackVenueData(venue || null);
      } catch (venueError) {
        setFallbackVenueData(null);
      }
    };

    loadVenueFallback();
  }, [booking]);

  useEffect(() => {
    const loadInvoice = async () => {
      if (!bookingId || !invoiceActorId) {
        setInvoiceMeta(null);
        return;
      }

      try {
        const files = await venueDashboardApiService.listBookingOwnerInvoices(bookingId, {
          userId: invoiceActorId
        });
        const readyFile =
          files.find((file) => String(file?.status || '').toLowerCase() === 'ready') || files[0];
        if (readyFile?.id) {
          setInvoiceMeta({
            fileId: readyFile.id,
            name: readyFile.filename || readyFile.name || 'Invoice'
          });
          return;
        }
      } catch (invoiceError) {
        // Fallback below handles cases where invoice endpoint is unavailable.
      }

      setInvoiceMeta(getInvoiceMetaFromBooking(booking));
    };

    loadInvoice();
  }, [bookingId, booking, invoiceActorId]);

  const statusBadge = useMemo(() => getOrganizerBookingBadge(booking), [booking]);
  const resolvedVenueImage = useMemo(
    () => getVenueImageFromSource(booking) || getVenueImageFromSource(fallbackVenueData) || '',
    [booking, fallbackVenueData]
  );
  const resolvedVenueAddress = useMemo(
    () => getVenueAddressFromSource(booking) || getVenueAddressFromSource(fallbackVenueData) || 'Address unavailable',
    [booking, fallbackVenueData]
  );

  const handleDownloadInvoice = async () => {
    if (!bookingId || !invoiceMeta || !invoiceActorId) return;

    if (invoiceMeta.url) {
      window.open(invoiceMeta.url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!invoiceMeta.fileId) return;

    try {
      setDownloadingInvoice(true);
      const response = await venueDashboardApiService.getBookingOwnerInvoiceDownloadUrl(
        bookingId,
        invoiceMeta.fileId,
        { userId: invoiceActorId }
      );
      if (response?.download_url) {
        window.open(response.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (downloadError) {
      setError('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const extractThreadId = (threadPayload) =>
    threadPayload?.thread_id ||
    threadPayload?.id ||
    threadPayload?.data?.thread_id ||
    threadPayload?.data?.id ||
    null;

  const matchesBookingThread = (thread) => {
    const threadBookingId =
      thread?.booking_id ||
      thread?.bookingId ||
      thread?.booking_reference ||
      thread?.bookingReference ||
      '';
    return normalizeIdentity(threadBookingId) === normalizeIdentity(bookingId);
  };

  const resolveExistingThread = async () => {
    if (!booking?.venueId || !bookingId || !invoiceActorId) return null;

    const bookingThreads = await messageThreadsApiService.listThreads({
      userId: invoiceActorId,
      bookingId,
      status: '',
      limit: 20
    });
    if (bookingThreads.length > 0) {
      return bookingThreads.find(matchesBookingThread) || bookingThreads[0];
    }

    const venueThreads = await messageThreadsApiService.listThreads({
      userId: invoiceActorId,
      venueId: booking.venueId,
      status: '',
      limit: 20
    });
    if (venueThreads.length > 0) {
      return venueThreads.find(matchesBookingThread) || null;
    }

    return null;
  };

  const handleOpenMessageModal = () => {
    setError('');
    setIsMessageModalOpen(true);
  };

  const handleCloseMessageModal = () => {
    if (openingMessageThread) return;
    setIsMessageModalOpen(false);
    setMessageDraft('');
  };

  const handleSubmitVenueMessage = async () => {
    if (!booking?.venueId || !bookingId || !invoiceActorId || openingMessageThread) return;
    if (!messageDraft.trim()) {
      setError('Please write a message before sending.');
      return;
    }

    try {
      setError('');
      setOpeningMessageThread(true);

      let selectedThread = await resolveExistingThread();

      if (!selectedThread) {
        const bookingLabel = booking.bookingReference || bookingId.slice(0, 8);
        const topic = buildBookingThreadTopic({
          bookingReference: booking.bookingReference || bookingLabel,
          eventName: booking.eventName,
          organizerName: booking.organizerName || booking.customerName,
          venueName: booking.venueName
        });
        let created = null;
        try {
          created = await messageThreadsApiService.createThread({
            venueId: booking.venueId,
            topic,
            bookingId,
            createdByUserId: invoiceActorId
          });
        } catch (createWithBookingError) {
          created = await messageThreadsApiService.createThread({
            venueId: booking.venueId,
            topic,
            createdByUserId: invoiceActorId
          });
        }
        const createdId = extractThreadId(created);
        selectedThread = createdId ? { id: createdId } : null;
      }

      if (!selectedThread?.id) {
        throw new Error('Unable to open a message thread for this venue.');
      }

      await messageThreadsApiService.sendMessage(selectedThread.id, messageDraft.trim(), {
        userId: invoiceActorId,
        senderId: invoiceActorId
      });

      setIsMessageModalOpen(false);
      setMessageDraft('');
      navigate(`/messages?threadId=${encodeURIComponent(selectedThread.id)}&source=booking&bookingId=${encodeURIComponent(bookingId)}`);
    } catch (threadError) {
      setError(threadError?.message || 'Failed to open message thread.');
    } finally {
      setOpeningMessageThread(false);
    }
  };

  if (authLoading) {
    return null;
  }

  if (!currentUser && paymentToken) {
    return null;
  }

  if (!currentUser) {
    return null;
  }

  if (loading || accountMismatch) {
    return (
      <OrganizerLayout activeNav="bookings">
        <div className="org-loading">
          <div className="org-loading-spinner" />
          <p>Loading booking details...</p>
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout activeNav="bookings">
      <div className="org-detail">
        <button
          type="button"
          className="org-detail-back"
          onClick={handleBackToBookings}
        >
          <IconArrowLeft />
          Back to bookings
        </button>

        {error ? (
          <div className="org-message org-message--error">
            <span>{error}</span>
          </div>
        ) : null}

        {isAdminView ? (
          <div className="org-message" style={{ marginBottom: 16 }}>
            <span>
              Admin view — showing booking as organiser{' '}
              <strong>{adminViewUserId}</strong>.
            </span>
          </div>
        ) : null}

        {loading ? (
          <div className="org-loading">
            <div className="org-loading-spinner" />
            <p>Loading booking details...</p>
          </div>
        ) : null}

        {!loading && booking ? (
          <>
            {inquirySentVisible ? (
              <div className="org-message org-message--success org-booking-inquiry-sent">
                <strong>Booking inquiry sent successfully</strong>
                <span>
                  We&apos;ve shared your request with the venue. You&apos;ll hear back once they review it.
                </span>
              </div>
            ) : null}

            {pendingDepositDue && fromBookingSubmit && !inquirySentVisible ? (
              <div className="org-deposit-checkout">
                <h2 className="org-deposit-checkout__title">Pay your deposit to send your request</h2>
                <p className="org-deposit-checkout__lead">
                  This venue requires a deposit before we share your inquiry with them. Pay securely with Stripe
                  now; the remaining balance (including card processing) is due after the venue approves.
                </p>
                {!accountMismatch && paymentContext?.mode === 'occasia_stripe' ? (
                  <BookingStripePayment
                    bookingId={bookingId}
                    booking={booking}
                    bookingStatus={booking.status}
                    autoOpen
                    paymentToken={stripePaymentToken}
                    onStatusChange={(statusPayload) => {
                      const nextStatus =
                        statusPayload?.status ||
                        statusPayload?.payment_status ||
                        statusPayload?.stripe?.status;
                      if (nextStatus) {
                        setPaymentStatusLabel(formatPaymentStatusLabel(nextStatus));
                      }
                      if (isDepositPaidBookingPaymentStatus(nextStatus)) {
                        handleDepositPaymentComplete();
                      }
                    }}
                    onPaymentComplete={handleDepositPaymentComplete}
                  />
                ) : null}
              </div>
            ) : null}

            <div className="org-detail-hero">
              {resolvedVenueImage ? (
                <img src={resolvedVenueImage} alt={booking.venueName || 'Venue'} className="org-detail-hero-image" />
              ) : (
                <div className="org-detail-hero-image org-detail-hero-image--placeholder" />
              )}
              <div className="org-detail-hero-content">
                <h1>{booking.venueName || 'Venue booking'}</h1>
                <p className="org-detail-hero-address">{resolvedVenueAddress}</p>
              </div>
            </div>

            <div className="org-detail-tabs-row">
              <div className="org-detail-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={detailTab === 'details'}
                  className={`org-detail-tab ${detailTab === 'details' ? 'org-detail-tab--active' : ''}`}
                  onClick={() => setDetailTab('details')}
                >
                  Booking details
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={detailTab === 'venue'}
                  className={`org-detail-tab ${detailTab === 'venue' ? 'org-detail-tab--active' : ''}`}
                  onClick={() => setDetailTab('venue')}
                >
                  About the venue
                </button>
              </div>
              <button
                type="button"
                className="org-detail-message-btn"
                onClick={handleOpenMessageModal}
                disabled={!booking.venueId || !invoiceActorId || openingMessageThread}
                aria-label={`Message ${booking.venueName || 'venue'}`}
              >
                <span className="org-detail-message-btn-icon" aria-hidden="true">
                  <IconMessage />
                </span>
                <span className="org-detail-message-btn-copy">
                  <span className="org-detail-message-btn-label">Message venue</span>
                  <span className="org-detail-message-btn-venue">{booking.venueName || 'Venue'}</span>
                </span>
              </button>
            </div>

            {detailTab === 'venue' ? (
              <div className="org-detail-panel">
                <div className="org-detail-panel-section">
                  <p className="org-detail-hero-address">{resolvedVenueAddress}</p>
                  {booking.venueId ? (
                    <button
                      type="button"
                      className="org-btn org-btn--secondary"
                      style={{ marginTop: 16 }}
                      onClick={() =>
                        navigate(
                          buildVenuePath({
                            id: booking.venueId,
                            name: booking.venueName,
                          })
                        )
                      }
                    >
                      View venue page
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="org-detail-layout">
                <div className="org-detail-main">
                  <div className="org-detail-panel">
                    <div className="org-detail-panel-section">
                      <div className="org-detail-panel-title-row">
                        <h2 className="org-detail-panel-title">
                          {booking.activity || booking.eventType || 'Event'}
                        </h2>
                        <span className="org-detail-hire-badge">Full venue hire</span>
                      </div>
                      <div className="org-home-meta-row">
                        <IconUsers />
                        <span>{booking.headcount || booking.attendees || 0} guests</span>
                      </div>
                      <div className="org-home-meta-row">
                        <IconCalendar />
                        <span>Single session</span>
                        <span className="org-home-meta-sep">/</span>
                        <span>{formatOrganizerDate(booking.date || booking.bookingDate)}</span>
                      </div>
                      <div className="org-home-meta-row">
                        <IconClock />
                        <span>{formatOrganizerTimeRange(booking.startTime, booking.endTime)}</span>
                        {booking.duration ? (
                          <>
                            <span className="org-home-meta-sep">/</span>
                            <span>{booking.duration} hours</span>
                          </>
                        ) : null}
                      </div>
                    </div>

                    <div className="org-detail-panel-section">
                      <p className="org-detail-panel-subtitle">
                        <IconProfile />
                        Basic details
                      </p>
                      <div className="org-home-detail-grid">
                        <div className="org-home-detail-item">
                          <span className="org-home-detail-label">Catering</span>
                          <span className="org-home-detail-value">
                            {formatBookingCatering(booking) === '—'
                              ? 'Not specified'
                              : formatBookingCatering(booking)}
                          </span>
                        </div>
                        {isInHouseCateringPreference(
                          booking?.requirements?.catering_preference ??
                            booking?.requirements?.catering ??
                            booking?.catering
                        ) ? (
                          <div className="org-home-detail-item">
                            <span className="org-home-detail-label">Dietary restrictions</span>
                            <span className="org-home-detail-value">
                              {formatBookingDietaryRestrictions(booking)}
                            </span>
                          </div>
                        ) : null}
                        <div className="org-home-detail-item">
                          <span className="org-home-detail-label">Audio/visual (A/V)</span>
                          <span className="org-home-detail-value">
                            {formatBookingAv(booking)}
                          </span>
                        </div>
                        <div className="org-home-detail-item">
                          <span className="org-home-detail-label">Alcohol</span>
                          <span className="org-home-detail-value">
                            {booking.alcohol ||
                              booking.requirements?.alcohol_consumption ||
                              booking.requirements?.alcohol ||
                              'Not specified'}
                          </span>
                        </div>
                        <div className="org-home-detail-item">
                          <span className="org-home-detail-label">Seating</span>
                          <span className="org-home-detail-value">
                            {booking.seating ||
                              booking.requirements?.layout ||
                              booking.requirements?.seating ||
                              'Not specified'}
                          </span>
                        </div>
                      </div>
                      {booking.specialRequests || bookingNotes.length > 0 ? (
                        <div className="org-home-notes" style={{ marginTop: 24 }}>
                          <span className="org-home-detail-label">Any other information</span>
                          {bookingNotes.length > 0 ? (
                            <ul>
                              {bookingNotes.map((note) => (
                                <li key={note.id || note.created_at}>{note.body}</li>
                              ))}
                            </ul>
                          ) : (
                            <p>{booking.specialRequests}</p>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {Array.isArray(booking.addOns) && booking.addOns.length > 0 ? (
                      <div className="org-detail-panel-section">
                        <p className="org-detail-panel-subtitle">Add ons</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {booking.addOns.map((addOn, index) => (
                            <div key={addOn.id || addOn.name || index} className="org-detail-addon-row">
                              <span className="org-detail-addon-name">{addOn.name || addOn.title}</span>
                              <span className="org-detail-addon-price">
                                {formatOrganizerCurrency(addOn.price || addOn.unit_price)}
                                {addOn.price_type === 'per_person' || addOn.perPerson ? (
                                  <small> / person</small>
                                ) : null}
                              </span>
                              <span className="org-detail-addon-name">{addOn.quantity || 1}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="org-detail-panel">
                    <div className="org-detail-panel-section">
                      <div className="org-detail-cancel-block">
                        <div>
                          <h3>Cancel booking</h3>
                          <p>
                            Changed your mind about hosting your event at {booking.venueName || 'this venue'}?
                            Cancel your booking anytime.
                          </p>
                        </div>
                        <button type="button" className="org-btn org-btn--danger org-btn--compact">
                          Cancel
                        </button>
                      </div>
                    </div>
                    {booking.cancellationPolicy ? (
                      <div className="org-detail-policy-wrap">
                        <div className="org-detail-policy-inner">
                          <h4>Cancellation policy</h4>
                          <p style={{ margin: 0, fontSize: 14, color: '#585858' }}>
                            {booking.cancellationPolicy}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <aside className="org-detail-sidebar">
                  <div className="org-detail-panel">
                    <div className="org-detail-sidebar-header">
                      <div className="org-detail-sidebar-header-title">
                        <IconBookings />
                        <span>Booking Status</span>
                      </div>
                      <span className={`org-badge org-badge--${statusBadge.variant}`}>
                        {statusBadge.label}
                      </span>
                    </div>

                    <div className="org-detail-price-rows">
                      <p className="org-detail-panel-subtitle" style={{ marginBottom: 0 }}>
                        <IconProfile />
                        Price breakdown
                      </p>

                      {venueApproved ? (
                        bookingIsFree ? (
                          <p>This booking is free — no payment is required.</p>
                        ) : bookingIsTbc ? (
                          <p>Awaiting price confirmation from the venue.</p>
                        ) : (
                          <>
                            <div className="org-detail-price-row">
                              <span>Venue total</span>
                              <strong>{formatOrganizerCurrency(booking.venueTotal)}</strong>
                            </div>
                            {booking.platformFee > 0 ? (
                              <div className="org-detail-price-row">
                                <span>Platform fee</span>
                                <strong>{formatOrganizerCurrency(booking.platformFee)}</strong>
                              </div>
                            ) : null}
                            {booking.processingFee > 0 ? (
                              <div className="org-detail-price-row">
                                <span>Processing fee</span>
                                <strong>{formatOrganizerCurrency(booking.processingFee)}</strong>
                              </div>
                            ) : null}
                            <hr className="org-detail-price-divider" />
                            <div className="org-detail-price-row org-detail-price-row--total">
                              <span>Total payable</span>
                              <strong>{formatOrganizerCurrency(booking.totalPrice)}</strong>
                            </div>
                          </>
                        )
                      ) : (
                        <p>Pricing will appear once the venue approves your booking.</p>
                      )}

                      {(paymentContext?.mode === 'occasia_stripe' ||
                        (stripePaymentDue && !bookingIsFree && paymentContext?.mode === 'free')) &&
                      stripePaymentDue &&
                      !accountMismatch &&
                      !(pendingDepositDue && fromBookingSubmit && !inquirySentVisible) ? (
                        <div style={{ width: '100%' }}>
                          <BookingStripePayment
                            bookingId={bookingId}
                            booking={booking}
                            bookingStatus={booking.status}
                            autoOpen={autoOpenPayment}
                            paymentToken={stripePaymentToken}
                            onStatusChange={(statusPayload) => {
                              const nextStatus =
                                statusPayload?.status ||
                                statusPayload?.payment_status ||
                                statusPayload?.stripe?.status;
                              if (nextStatus) {
                                setPaymentStatusLabel(formatPaymentStatusLabel(nextStatus));
                              }
                              if (isDepositPaidBookingPaymentStatus(nextStatus)) {
                                handleDepositPaymentComplete();
                              }
                            }}
                            onPaymentComplete={handleDepositPaymentComplete}
                          />
                        </div>
                      ) : paymentContext?.mode === 'external_link' &&
                        isApprovedBookingStatus(booking.status) &&
                        needsPayment ? (
                        <a
                          className="org-btn org-btn--primary org-btn--block"
                          href={paymentContext.link}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Complete payment
                        </a>
                      ) : paymentContext?.mode === 'invoice' &&
                        isApprovedBookingStatus(booking.status) &&
                        needsPayment ? (
                        <BookingInvoicePayment
                          amount={booking.amountPending > 0 ? booking.amountPending : booking.totalPrice}
                          paymentStatus={paymentStatusLabel || booking.paymentStatus}
                          hasInvoice={Boolean(invoiceMeta)}
                          onDownloadInvoice={handleDownloadInvoice}
                          downloading={downloadingInvoice}
                        />
                      ) : venueApproved && needsPayment && !bookingIsFree && !bookingIsTbc && !accountMismatch ? (
                        <button
                          type="button"
                          className="org-btn org-btn--primary org-btn--block"
                          onClick={() => navigate(`/profile/bookings/${bookingId}?pay=stripe`)}
                        >
                          Pay {formatOrganizerCurrency(booking.amountPending || booking.totalPrice)}
                        </button>
                      ) : null}

                      {venueApproved && invoiceMeta ? (
                        <button
                          type="button"
                          className="org-btn org-btn--secondary org-btn--block"
                          onClick={handleDownloadInvoice}
                          disabled={downloadingInvoice}
                        >
                          {downloadingInvoice ? 'Preparing invoice...' : 'Download invoice'}
                        </button>
                      ) : null}

                      <div className="org-detail-stripe-note">
                        <span>Powered by</span>
                        <img src="/manual-booking/icons/stripe-logo.png" alt="Stripe" />
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </>
        ) : null}
      </div>

      {isMessageModalOpen ? (
        <div className="profile-message-modal-overlay" role="dialog" aria-modal="true">
          <div className="profile-message-modal">
            <h3>Message venue</h3>
            <p className="profile-message-modal-subtitle">
              Write your message. We will create or use the booking thread only after you send.
            </p>
            <textarea
              className="profile-form-textarea"
              value={messageDraft}
              onChange={(e) => setMessageDraft(e.target.value)}
              placeholder="Type your message here"
              rows={5}
              disabled={openingMessageThread}
            />
            <div className="profile-message-modal-actions">
              <button
                type="button"
                className="booking-view-details-button"
                onClick={handleCloseMessageModal}
                disabled={openingMessageThread}
              >
                Cancel
              </button>
              <button
                type="button"
                className="booking-view-details-button booking-view-details-button--primary"
                onClick={handleSubmitVenueMessage}
                disabled={openingMessageThread || !messageDraft.trim()}
              >
                {openingMessageThread ? 'Sending...' : 'Send message'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </OrganizerLayout>
  );
};

export default BookingDetails;
